import React, { useEffect, useState } from 'react';
import { ReturnHeaders } from '@src/components/shared/ReturnHeaders';
import { ReturnButtons } from '@src/components/shared/ReturnButtons';
import {
  goToPreviousStep,
  goToNextStep,
  setReturnMethods,
} from '@src/stores/newFlowStore';
import {
  setReturnShipments,
  setSelectedItems,
  useReturnStore,
} from '@src/stores/returnStore';
import {
  MAX_WEIGHT_LIMIT,
  RETURN_METHOD,
  ReturnMethodErrorType,
  ReturnMethodType,
  isClientSideOnlyMethod,
} from '@src/constants/general';
import OrderReturnMethodOptions from '@src/components/return-method/OrderReturnMethodOptions';
import SplitReturnMethodOptions from '@src/components/return-method/SplitReturnMethodOptions';
import {
  buildRefundEstimationRequest,
  checkFraudType,
  generateInStoreShipment,
  PolicyValidatorErrors,
} from '@src/utils';
import {
  disableAbandonmentSurvey,
  enableLORPAbandonmentSurvey,
  enableReturnAbandonmentSurvey,
} from '@src/utils/storage-helper';
import { isCookieEnabled } from '@src/utils/cookie-helper';
import { isProdEnvironment } from '@src/config/timeout';
import { fetchRefundEstimationAPI } from '@src/utils/api-helpers';
import { setRefundValues, useSummaryStore } from '@src/stores/summaryStore';
import {
  getFeatureSwitchStatic,
  getTotalUPSWeight,
  getUniqueMethods,
} from '@src/utils/general';
import { useProcessValidator } from '@src/hooks/useProcessValidator';
import ErrorBanner from '@src/components/shared/ErrorBanner';
import FraudModal from '@src/components/shared/fraud/FraudModal';
import PolicyValidatorAlert from '@src/components/select-items/PolicyValidatorAlert';
import useScrollableError from '@src/hooks/useScrollableError';
import WholeOrderMethodError from '@src/components/return-method/errors/WholeOrderMethodError';
import { ItemToReturn, SelectedCardType } from '@src/models/returns';
import {
  TagOnOrderSelectPerItemMethodChosen,
  TagOnReturnMethodPerItemChosen,
  TagReturnQuantityReasonSubmission,
  TagReturnMethodContinue,
} from '@src/utils/analytics-tagging';
import { getReturnMethodSelectionMode } from '@src/utils/returnFlow';

const IN_STORE_QTY_REASON_SETTINGS =
  'in store return quantity and reason selected';

const isWholeOrderQuantityReasonMethod = (
  method: SelectedCardType,
): method is ReturnMethodType =>
  method === RETURN_METHOD.IN_STORE ||
  method === RETURN_METHOD.SORIS ||
  method === RETURN_METHOD.UPS_DROP_OFF ||
  method === RETURN_METHOD.LORP;

const getWholeOrderQuantityReasonSettings = (method: ReturnMethodType) =>
  method === RETURN_METHOD.IN_STORE || method === RETURN_METHOD.SORIS
    ? IN_STORE_QTY_REASON_SETTINGS
    : undefined;

const SelectReturnMethodStep = () => {
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );
  const refundValues = useSummaryStore((state) => state.refundValues);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const militaryExchange = useReturnStore((state) => state.militaryExchange);

  // For returns that want to return less than 4 SKU items, we will skip the WholeOrderStep step and go straight to PerItemStep
  // Exception: APO/FPO eligible orders always use WholeOrderStep (no per-item selection)
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const { isApoFpoEligible, shouldSkipFirstStep, isStandardWholeOrderFlow } =
    getReturnMethodSelectionMode(selectedItems);

  // Change default to no pre-selection
  const initialSelectedCard: SelectedCardType = '';
  const [selectedCard, setSelectedCard] =
    useState<SelectedCardType>(initialSelectedCard);
  const [step, setStep] = useState<'WholeOrderStep' | 'PerItemStep'>(
    shouldSkipFirstStep ? 'PerItemStep' : 'WholeOrderStep',
  );

  // Scrollable error for whole order step
  const {
    errorRef: wholeOrderErrorRef,
    showAndScrollToError: showAndScrollToWholeOrderError,
    showError: showWholeOrderErrorWithScroll,
    hideScrollableError: hideWholeOrderError,
    errorType: wholeOrderErrorType,
  } = useScrollableError<ReturnMethodErrorType>();

  // Scrollable error for per-item step
  const {
    errorRef: perItemErrorRef,
    showAndScrollToFirstError: showAndScrollToFirstPerItemError,
    showError: showPerItemErrorsWithScroll,
    hideScrollableError: hidePerItemErrors,
  } = useScrollableError('[data-testid="item-return-method-error"]');

  // past selections
  const [stringRefundRequest, setStringRefundRequest] = useState<string>('');

  // could remove this alert state if not needed anymore
  const alertState = useReturnStore((state) => state.alertState);

  const proceedToNextStep = () => {
    if (!getFeatureSwitchStatic('enableRefundCalculator')) {
      setRefundValues(undefined);
      goToNextStep();
      return;
    }

    const refundRequest = buildRefundEstimationRequest(
      orderDetailsV2,
      selectedItems,
    );
    const newStringRefundRequest = JSON.stringify(refundRequest.lineItems);
    if (refundValues && stringRefundRequest === newStringRefundRequest) {
      goToNextStep();
      return;
    }
    setStringRefundRequest(newStringRefundRequest);
    fetchRefundEstimationAPI(refundRequest).then((refundAmount) =>
      setRefundValues(refundAmount),
    );
    goToNextStep();
  };

  // PolicyValidator
  const {
    processPolicyValidator,
    renderModal,
    returnFraudType,
    policyValidatorAlert,
    setPolicyValidatorAlert,
    isPolicyValidatorLoading,
  } = useProcessValidator({
    orderDetailsV2,
    returnEligibilityDetails,
    isMilitaryExchangeGuest,
    proceedToNextStep,
  });

  const handleContinueButton = () => {
    hideWholeOrderError();
    // Only check selectedCard if we're on WholeOrderStep
    if (step === 'WholeOrderStep' && selectedCard === '') {
      showAndScrollToWholeOrderError();
      return;
    }

    if (
      step === 'WholeOrderStep' &&
      selectedCard !== 'Split' &&
      selectedCard !== ''
    ) {
      setReturnMethods([selectedCard]);
      const modifiedItems = selectedItems.map((item) => ({
        ...item,
        returnMethod: selectedCard,
      }));
      setSelectedItems(modifiedItems);
      if (
        isStandardWholeOrderFlow &&
        isWholeOrderQuantityReasonMethod(selectedCard)
      ) {
        TagReturnQuantityReasonSubmission(
          modifiedItems,
          militaryExchange,
          getWholeOrderQuantityReasonSettings(selectedCard),
        );
      }
      onStepFinish(modifiedItems);
      TagReturnMethodContinue(selectedCard);
      return;
    }

    if (step === 'PerItemStep') {
      if (selectedItems.some((item) => !item.returnMethod)) {
        showAndScrollToFirstPerItemError();
        return;
      }
      if (getTotalUPSWeight(selectedItems) >= MAX_WEIGHT_LIMIT) {
        showAndScrollToWholeOrderError(
          ReturnMethodErrorType.WEIGHT_LIMIT_EXCEEDED,
        );
        return;
      }

      setReturnMethods(getUniqueMethods(selectedItems));
      onStepFinish(selectedItems);
      if (isStandardWholeOrderFlow && selectedCard === 'Split') {
        TagReturnQuantityReasonSubmission(selectedItems, militaryExchange);
      }
      TagOnReturnMethodPerItemChosen(selectedItems);
      return;
    }

    // selectedSplit but still at step 1
    setStep('PerItemStep');
    TagOnOrderSelectPerItemMethodChosen();
  };

  const handleBackButton = () => {
    if (step === 'PerItemStep' && !shouldSkipFirstStep) {
      setStep('WholeOrderStep');
      return;
    }
    goToPreviousStep();
  };

  const onStepFinish = async (modifiedItems: ItemToReturn[]) => {
    // Check if all items are client-side only (IN_STORE or APO/FPO) - if so, skip Policy Validator entirely
    const hasAllClientSideItems = modifiedItems.every((item) =>
      isClientSideOnlyMethod(item.returnMethod),
    );

    // Enable abandonment survey based on selected return method(s)
    // Use LORP survey only when ALL items are LORP
    // Disable survey for all IN_STORE/APO_FPO items
    // Otherwise default to return survey (UPS, SORIS, or mixed)
    // Skip survey logic if qaSurveyPass cookie is set in non-prod (for QA regression testing)
    const isQaSurveyBypass =
      !isProdEnvironment() && isCookieEnabled('qaSurveyPass');
    if (!isQaSurveyBypass) {
      const allLORP = modifiedItems.every(
        (item) => item.returnMethod === RETURN_METHOD.LORP,
      );
      if (hasAllClientSideItems) {
        disableAbandonmentSurvey();
      } else if (allLORP) {
        enableLORPAbandonmentSurvey();
      } else {
        enableReturnAbandonmentSurvey();
      }
    }

    if (hasAllClientSideItems) {
      // All items are client-side only (IN_STORE or APO/FPO), no need for Policy Validator call
      // Only IN_STORE needs a pseudo-shipment; APO/FPO doesn't use returnShipments
      if (
        modifiedItems.some(
          (item) => item.returnMethod === RETURN_METHOD.IN_STORE,
        )
      ) {
        setReturnShipments([generateInStoreShipment(modifiedItems, [])]);
      }
      proceedToNextStep();
      return;
    }

    // If ups drop off is selected, we need to validate item eligibility before proceeding
    const policyResponse = await processPolicyValidator(modifiedItems);
    if (!policyResponse) return;
    // Check for fraud has been done before, so only proceeding in case no Modal was shown.
    const verifyDecision = policyResponse?.returnService?.multiVerifyDecision;
    const fraudType = checkFraudType(verifyDecision);
    if (!fraudType) {
      proceedToNextStep();
    }
  };

  useEffect(() => {
    setStep(shouldSkipFirstStep ? 'PerItemStep' : 'WholeOrderStep');
    if (shouldSkipFirstStep) {
      TagOnOrderSelectPerItemMethodChosen();
    }
  }, [shouldSkipFirstStep, isApoFpoEligible]);

  // Hide errors when user makes selections
  useEffect(() => {
    if (selectedCard !== '') {
      hideWholeOrderError();
    }
  }, [selectedCard]);

  useEffect(() => {
    if (
      step === 'PerItemStep' &&
      selectedItems.every((item) => item.returnMethod)
    ) {
      hidePerItemErrors();
    }
  }, [selectedItems, step]);

  return (
    <div>
      <ReturnHeaders title="Select your return method" />
      {policyValidatorAlert && (
        <PolicyValidatorAlert
          alert={policyValidatorAlert}
          clearAlert={() => setPolicyValidatorAlert(PolicyValidatorErrors.NONE)}
        />
      )}
      {alertState?.type === 'NETWORK' && <ErrorBanner />}

      <WholeOrderMethodError
        ref={wholeOrderErrorRef}
        showError={showWholeOrderErrorWithScroll}
        type={wholeOrderErrorType}
      />

      {step === 'WholeOrderStep' && (
        <>
          <OrderReturnMethodOptions
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
          />
        </>
      )}
      {step === 'PerItemStep' && (
        <SplitReturnMethodOptions
          showItemErrors={showPerItemErrorsWithScroll}
          ref={perItemErrorRef}
        />
      )}
      <ReturnButtons
        proceedNext={handleContinueButton}
        loading={isPolicyValidatorLoading}
        proceedBack={handleBackButton}
      />
      {renderModal && (
        <FraudModal
          returnFraudType={returnFraudType}
          title={renderModal.title}
          body={renderModal.bodyMessage}
          buttonText={renderModal.buttonMessage}
          onClick={renderModal.onClick}
        />
      )}
    </div>
  );
};

export default SelectReturnMethodStep;
